// Copyright (c) 2025 by Audere

import { celosiaLabelingSchemaV1 } from "../features/data/schema/celosiaLabelingSchemaV1";
import { celosiaLabelingSchemaV2 } from "../features/data/schema/celosiaLabelingSchemaV2";
import { celosiaLabelingSchemaV3 } from "../features/data/schema/celosiaLabelingSchemaV3";
import { cloverLabelingSchemaV1 } from "../features/data/schema/cloverLabelingSchemaV1";
import { cloverLabelingSchemaV2 } from "../features/data/schema/cloverLabelingSchemaV2";
import { cloverLabelingSchemaV3 } from "../features/data/schema/cloverLabelingSchemaV3";
import { mangroveLabelingSchemaV1 } from "../features/data/schema/mangroveLabelingSchemaV1";
import { mangroveLabelingSchemaV2 } from "../features/data/schema/mangroveLabelingSchemaV2";
import { mangroveLabelingSchemaV3 } from "../features/data/schema/mangroveLabelingSchemaV3";
import { proteaLabelingSchemaV1 } from "../features/data/schema/proteaLabelingSchemaV1";
import { proteaLabelingSchemaV2 } from "../features/data/schema/proteaLabelingSchemaV2";
import { proteaLabelingSchemaV3 } from "../features/data/schema/proteaLabelingSchemaV3";
import { LabelingSchema } from "../features/label/labelTypes";
import { useAppSelector } from "../features/store";
import { useCurrentUser } from "./useCurrentUser";

const schemaVersionMap: Record<string, LabelingSchema> = {
  cloverLabelingSchemaV1,
  proteaLabelingSchemaV1,
  celosiaLabelingSchemaV1,
  mangroveLabelingSchemaV1,
  cloverLabelingSchemaV2,
  proteaLabelingSchemaV2,
  celosiaLabelingSchemaV2,
  mangroveLabelingSchemaV2,
  proteaLabelingSchemaV3,
  celosiaLabelingSchemaV3,
  mangroveLabelingSchemaV3,
  cloverLabelingSchemaV3,
};

// List of all patient groups
// https://github.com/AudereNow/audere/blob/main/EpistoreLambda/src/epistore/client_record/id.py
const patientGroupSchemaConfig: Record<string, LabelingSchema> = {
  "2025-02-SouthAfrica-clover-FieldStudy": cloverLabelingSchemaV3,
  "2025-02-SouthAfrica-clover-FieldStudy-demo": cloverLabelingSchemaV3,
  "2025-02-SouthAfrica-clover-FieldStudy-test": cloverLabelingSchemaV3,
  "2025-04-SouthAfrica-protea": proteaLabelingSchemaV3,
  "2025-04-SouthAfrica-protea-demo": proteaLabelingSchemaV3,
  "2025-04-SouthAfrica-protea-test": proteaLabelingSchemaV3,
  "2025-04-Zimbabwe-celosia-UsabilityStudy": celosiaLabelingSchemaV3,
  "2025-04-Zimbabwe-celosia-UsabilityStudy-demo": celosiaLabelingSchemaV3,
  "2025-04-Zimbabwe-celosia-UsabilityStudy-test": celosiaLabelingSchemaV3,
  "2025-04-SouthAfrica-mangrove-UsabilityStudy": mangroveLabelingSchemaV3,
  "2025-04-SouthAfrica-mangrove-UsabilityStudy-demo": mangroveLabelingSchemaV3,
  "2025-04-SouthAfrica-mangrove-UsabilityStudy-test": mangroveLabelingSchemaV3,
  "2025-09-SouthAfrica-mangrove-FieldStudy": mangroveLabelingSchemaV3,
  "2025-09-SouthAfrica-mangrove-FieldStudy-demo": mangroveLabelingSchemaV3,
  "2025-09-SouthAfrica-mangrove-FieldStudy-test": mangroveLabelingSchemaV3,
  "2025-09-Zimbabwe-celosia-FieldStudy": celosiaLabelingSchemaV3,
  "2025-09-Zimbabwe-celosia-FieldStudy-demo": celosiaLabelingSchemaV3,
  "2025-09-Zimbabwe-celosia-FieldStudy-test": celosiaLabelingSchemaV3,
};

export const getSchemaByVersion = (
  schemaVersion?: string
): LabelingSchema | undefined => {
  if (!schemaVersion) {
    return undefined;
  }

  return schemaVersionMap[schemaVersion];
};

export const useLabelingSchema = (): LabelingSchema => {
  const { group } = useCurrentUser();

  const schemaVersion = useAppSelector((state) => state.label.schemaVersion);

  const versionedSchema = getSchemaByVersion(schemaVersion);
  if (versionedSchema) {
    return versionedSchema;
  }

  if (group in patientGroupSchemaConfig) {
    return patientGroupSchemaConfig[group];
  }

  return cloverLabelingSchemaV3;
};
